import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../config/api';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import DigestNotes from '../components/DigestNotes';
import VideoChatPanel from '../components/chat/VideoChatPanel';
import VideoThoughtPad from '../components/chat/VideoThoughtPad';
import { useVideoNotes } from '../hooks/useVideoNotes';
import { NoteType } from '../types/reading-notes';
import {
  ArrowLeftIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  Squares2X2Icon,
  SparklesIcon,
  LinkIcon,
  VideoCameraIcon,
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  CheckIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ListBulletIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

interface DigestBlock {
  type: string;
  anchor_sec?: number;
  title?: string;
  latex?: string;
}
interface DigestTopic {
  title: string;
  start_sec: number;
  end_sec: number;
  summary?: string;
  cues?: string[];
  takeaway?: string;
  blocks?: DigestBlock[];
}
interface LectureDigest {
  title?: string;
  tldr?: string;
  duration_sec?: number | null;
  objectives?: string[];
  topics?: DigestTopic[];
  formula_sheet?: DigestBlock[];
  key_terms?: DigestBlock[];
  common_pitfalls?: DigestBlock[];
  visuals?: DigestBlock[];
}
interface Video {
  id: string;
  title: string;
  source_type: 'upload' | 'youtube' | 'gdrive' | 'onedrive' | 'url';
  external_url?: string | null;
  playback_url?: string | null;
  book_title?: string | null;
  chapter_number?: number | null;
  concept_tags?: string[] | null;
  status: string;
  language?: string | null;
  duration_seconds?: number | null;
  processed_at?: string | null;
  error_message?: string | null;
  lecture_notes_md?: string | null;
  lecture_digest?: LectureDigest | null;
  progress_seconds?: number | null;
}

type TabKey = 'overview' | 'notes' | 'thoughtpad' | 'chat';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
const youTubeId = (url?: string | null): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};
const fmtTime = (s: number): string => {
  if (!s || s < 0 || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const x = Math.floor(s % 60);
  return `${m}:${String(x).padStart(2, '0')}`;
};

const VideoWatch: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Arrived from a note's timestamp → show "Back to note" instead of "Library".
  const fromNote = (location.state as any)?.fromNote === true;
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [video, setVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<TabKey>('overview');
  const [leftPct, setLeftPct] = useState(60);
  const [isFs, setIsFs] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [notesTopChapter, setNotesTopChapter] = useState<{ title: string; sec: number } | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);

  // Playback clock (drives progress %, active chapter, watch-aware highlight)
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const playerRef = useRef<HTMLDivElement>(null);
  const videoElRef = useRef<HTMLVideoElement>(null);
  const ytPlayerRef = useRef<any>(null);
  const notesScrollRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const currentTimeRef = useRef(0);
  const lastSavedRef = useRef(-1);
  const resumeRef = useRef(0);          // saved position to seek to once the player is ready
  const pauseFollowUntil = useRef(0);   // suppress auto-follow briefly after a manual scroll

  const digest = video?.lecture_digest || null;
  const topics = useMemo(() => digest?.topics || [], [digest]);
  const yt = video && video.source_type === 'youtube' ? youTubeId(video.external_url) : null;
  const isProcessing = video?.status === 'processing' || video?.status === 'uploaded';
  const isFailed = video?.status === 'failed';
  const hasDigest = !!(video?.lecture_notes_md || (topics.length > 0));

  // ── load + poll while processing ─────────────────────────────────────────
  const load = useCallback(() => {
    if (!videoId) return Promise.resolve();
    return api.get<Video>(`/api/videos/${videoId}`)
      .then((r) => setVideo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load video.'));
  }, [videoId]);

  useEffect(() => {
    if (!videoId) return;
    setLoading(true); setError('');
    load().finally(() => setLoading(false));
  }, [videoId, load]);

  useEffect(() => {
    if (!isProcessing) return;
    const t = setInterval(() => { load(); }, 6000);
    return () => clearInterval(t);
  }, [isProcessing, load]);

  // ── duration fallback (before the player reports it) ─────────────────────
  useEffect(() => {
    const fallback = video?.duration_seconds || digest?.duration_sec
      || (topics.length ? topics[topics.length - 1].end_sec : 0) || 0;
    if (fallback && !duration) setDuration(fallback);
  }, [video, digest, topics, duration]);

  // ── YouTube IFrame API: time tracking + seek ─────────────────────────────
  useEffect(() => {
    if (!yt) return;
    let cancelled = false;
    let poll: any;
    const setup = () => {
      const YT = (window as any).YT;
      if (cancelled || !YT?.Player || !document.getElementById('yt-frame')) return;
      ytPlayerRef.current = new YT.Player('yt-frame', {
        events: {
          onReady: (e: any) => {
            const d = e.target.getDuration?.(); if (d) setDuration(d);
            setMuted(!!e.target.isMuted?.());
            const r = resumeRef.current;
            if (r > 3 && (!d || r < d - 5)) { e.target.seekTo(r, true); setCurrentTime(r); }
          },
          onStateChange: (e: any) => {
            setPlaying(e.data === YT.PlayerState.PLAYING);
            if (e.data === YT.PlayerState.PLAYING) {
              clearInterval(poll);
              poll = setInterval(() => {
                const t = ytPlayerRef.current?.getCurrentTime?.();
                if (typeof t === 'number') setCurrentTime(t);
              }, 500);
            } else {
              clearInterval(poll);
              const t = ytPlayerRef.current?.getCurrentTime?.();
              if (typeof t === 'number') setCurrentTime(t);
            }
          },
        },
      });
    };
    if ((window as any).YT?.Player) {
      setTimeout(setup, 0);
    } else {
      const prev = (window as any).onYouTubeIframeAPIReady;
      (window as any).onYouTubeIframeAPIReady = () => { prev?.(); setup(); };
      if (!document.getElementById('yt-iframe-api')) {
        const s = document.createElement('script');
        s.id = 'yt-iframe-api';
        s.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(s);
      }
    }
    return () => { cancelled = true; clearInterval(poll); try { ytPlayerRef.current?.destroy?.(); } catch { /* noop */ } ytPlayerRef.current = null; };
  }, [yt]);

  // Stable identity — read the playhead through the ref so the chat panel's
  // props don't change on every timeupdate re-render (~4×/sec during
  // playback). An inline arrow here forced the whole chat subtree (markdown,
  // KaTeX, figures) to re-render continuously → visible flicker while the
  // video plays.
  const getPlayhead = useCallback(() => Math.floor(currentTimeRef.current), []);

  // Tab-capture fallback (Screen Capture API) — the only browser-sanctioned
  // way to read a YouTube iframe's pixels: ask the user to share THIS tab,
  // grab one frame, stop the stream immediately, crop to the player's
  // on-screen rect. Chrome offers a "share this tab" fast path
  // (preferCurrentTab); other browsers show their standard picker. If the
  // user shares a different surface the crop rect won't line up — the
  // SnipModal preview makes that obvious and they can cancel.
  const captureViaTabShare = useCallback(async (): Promise<string | null> => {
    const host = playerRef.current;
    if (!host || !navigator.mediaDevices?.getDisplayMedia) return null;
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        preferCurrentTab: true,
        video: true,
        audio: false,
      } as any);
      const grabber = document.createElement('video');
      grabber.srcObject = stream;
      grabber.muted = true;
      await grabber.play();
      await new Promise((r) => setTimeout(r, 150)); // let the first real frame land
      const full = document.createElement('canvas');
      full.width = grabber.videoWidth;
      full.height = grabber.videoHeight;
      full.getContext('2d')!.drawImage(grabber, 0, 0);
      // Map the player's CSS rect onto the captured surface (tab viewport at
      // device-pixel scale).
      const r = host.getBoundingClientRect();
      const sx = full.width / window.innerWidth;
      const sy = full.height / window.innerHeight;
      const crop = document.createElement('canvas');
      crop.width = Math.max(1, Math.round(r.width * sx));
      crop.height = Math.max(1, Math.round(r.height * sy));
      crop.getContext('2d')!.drawImage(
        full,
        r.left * sx, r.top * sy, r.width * sx, r.height * sy,
        0, 0, crop.width, crop.height,
      );
      return crop.toDataURL('image/jpeg', 0.92);
    } catch {
      return null; // user dismissed the share prompt, or unsupported browser
    } finally {
      stream?.getTracks().forEach((t) => t.stop());
    }
  }, []);

  // Snip: capture the PAUSED frame as a JPEG data URL for the chat panel.
  // Uploads: silent canvas grab off the <video> (CORS-clean case), then a
  // CORS-enabled offscreen retry. YouTube — or an upload whose stream refuses
  // CORS — falls back to tab capture above (one browser share-prompt per
  // snip; that permission step is mandated security UX, not removable).
  const captureFrame = useCallback(async (): Promise<string | null> => {
    if (yt) {
      ytPlayerRef.current?.pauseVideo?.();
      return captureViaTabShare();
    }
    const v = videoElRef.current;
    if (!v || !v.videoWidth) return null;
    v.pause();
    const grab = (el: HTMLVideoElement) => {
      const c = document.createElement('canvas');
      c.width = el.videoWidth;
      c.height = el.videoHeight;
      c.getContext('2d')!.drawImage(el, 0, 0);
      return c.toDataURL('image/jpeg', 0.92);
    };
    try {
      return grab(v);
    } catch { /* tainted canvas — retry with a CORS-enabled offscreen element */ }
    try {
      const off = document.createElement('video');
      off.crossOrigin = 'anonymous';
      off.muted = true;
      off.playsInline = true;
      off.preload = 'auto';
      off.src = v.currentSrc || v.src;
      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error('snip timeout')), 8000);
        off.onerror = () => { clearTimeout(t); reject(new Error('snip load failed')); };
        off.onseeked = () => { clearTimeout(t); resolve(); };
        off.onloadedmetadata = () => { off.currentTime = Math.max(0, currentTimeRef.current); };
      });
      return grab(off);
    } catch { /* stream refuses CORS — last resort: tab capture */ }
    return captureViaTabShare();
  }, [yt, captureViaTabShare]);

  const seek = useCallback((sec: number, play = true) => {
    if (yt && ytPlayerRef.current?.seekTo) {
      ytPlayerRef.current.seekTo(sec, true);
      if (play) ytPlayerRef.current.playVideo?.();
    } else if (videoElRef.current) {
      videoElRef.current.currentTime = sec;
      if (play) videoElRef.current.play?.().catch(() => { /* autoplay block */ });
    }
    setCurrentTime(sec);
  }, [yt]);

  const togglePlay = useCallback(() => {
    if (yt && ytPlayerRef.current?.getPlayerState) {
      ytPlayerRef.current.getPlayerState() === 1 ? ytPlayerRef.current.pauseVideo?.() : ytPlayerRef.current.playVideo?.();
    } else if (videoElRef.current) {
      videoElRef.current.paused ? videoElRef.current.play?.().catch(() => {}) : videoElRef.current.pause?.();
    }
  }, [yt]);

  const toggleMute = useCallback(() => {
    if (yt && ytPlayerRef.current) {
      const m = ytPlayerRef.current.isMuted?.();
      if (m) ytPlayerRef.current.unMute?.(); else ytPlayerRef.current.mute?.();
      setMuted(!m);
    } else if (videoElRef.current) {
      videoElRef.current.muted = !videoElRef.current.muted;
      setMuted(videoElRef.current.muted);
    }
  }, [yt]);

  // ── coordinated nav: seek the video + jump the open Notes to the section ──
  // Poll until the Notes panel is actually visible (it un-hides on the tab switch),
  // then scroll. Works on the first jump AND every subsequent click.
  const scrollToSection = useCallback((id: string) => {
    let tries = 0;
    const attempt = () => {
      const el = notesScrollRef.current?.querySelector(
        `#${(window as any).CSS?.escape ? CSS.escape(id) : id}`,
      ) as HTMLElement | null;
      if (el && el.offsetParent !== null) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (tries++ < 25) {
        setTimeout(attempt, 40);
      }
    };
    attempt();
  }, []);

  const navTo = useCallback((sec?: number, sectionId?: string) => {
    if (typeof sec === 'number' && sec >= 0) seek(sec);
    pauseFollowUntil.current = Date.now() + 2500;   // explicit jump wins over auto-follow
    const id = sectionId || (typeof sec === 'number' ? `sec-${Math.floor(sec)}` : null);
    setTab('notes');
    if (id) scrollToSection(id);
  }, [seek, scrollToSection]);

  // ── active chapter (watch-aware) ─────────────────────────────────────────
  const activeIdx = useMemo(() => {
    let idx = -1;
    for (let i = 0; i < topics.length; i++) if (currentTime + 0.5 >= topics[i].start_sec) idx = i;
    return idx;
  }, [topics, currentTime]);
  const activeTopic = activeIdx >= 0 ? topics[activeIdx] : null;
  const pct = duration ? Math.min(100, Math.round((currentTime / duration) * 100)) : 0;

  // Keep a ref of the clock so the throttled saver reads the latest without re-subscribing.
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);

  // Hold the saved resume position — the player seeks to it the instant it's ready (onReady /
  // onLoadedMetadata below), which is the only point the seek actually sticks.
  // A `?t=` deep-link (e.g. opened from a video note's timestamp) wins over the
  // saved progress.
  useEffect(() => {
    const t = Number(searchParams.get('t'));
    resumeRef.current = Number.isFinite(t) && t > 0 ? t : (video?.progress_seconds || 0);
  }, [video, searchParams]);

  // Persist playback position: throttled while watching + on leave.
  useEffect(() => {
    if (!videoId) return;
    const save = () => {
      const p = Math.floor(currentTimeRef.current);
      if (p > 0 && p !== lastSavedRef.current) {
        lastSavedRef.current = p;
        api.put(`/api/videos/${videoId}/progress`, { position_seconds: p }).catch(() => { /* best-effort */ });
      }
    };
    const iv = setInterval(save, 8000);
    window.addEventListener('pagehide', save);
    return () => { clearInterval(iv); window.removeEventListener('pagehide', save); save(); };
  }, [videoId]);

  // Zoom-style auto-follow: as the lecture plays, scroll the open Notes to the active topic
  // (paused for a few seconds after the user scrolls manually, so we don't fight them).
  useEffect(() => {
    if (tab !== 'notes' || activeIdx < 0 || Date.now() < pauseFollowUntil.current) return;
    const t = topics[activeIdx];
    if (!t) return;
    const el = notesScrollRef.current?.querySelector(`#sec-${Math.floor(t.start_sec)}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeIdx, tab, topics]);

  // Keyboard: space = play/pause, ← / → = previous / next chapter.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.key === ' ' || e.code === 'Space') { e.preventDefault(); togglePlay(); }
      else if (e.key === 'ArrowRight') { const n = topics[activeIdx + 1]; if (n) { e.preventDefault(); seek(n.start_sec); } }
      else if (e.key === 'ArrowLeft') { const p = topics[Math.max(0, activeIdx - 1)]; if (p) { e.preventDefault(); seek(p.start_sec); } }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, seek, topics, activeIdx]);

  // Scroll-spy: which chapter is at the top of the Notes viewport (sticky header).
  const handleNotesScroll = useCallback(() => {
    const root = notesScrollRef.current;
    if (!root) return;
    let top: { title: string; sec: number } | null = null;
    for (const t of topics) {
      const el = root.querySelector(`#sec-${Math.floor(t.start_sec)}`) as HTMLElement | null;
      if (el && el.offsetTop - root.scrollTop <= 52) top = { title: t.title, sec: t.start_sec };
    }
    setNotesTopChapter((prev) => (prev?.sec === top?.sec ? prev : top));
  }, [topics]);

  // Initialize the sticky chapter header when the Notes tab opens.
  useEffect(() => { if (tab === 'notes') handleNotesScroll(); }, [tab, handleNotesScroll]);

  // ── resizable divider / fullscreen ───────────────────────────────────────
  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!dragging.current) return;
      setLeftPct(Math.min(74, Math.max(42, (e.clientX / window.innerWidth) * 100)));
    };
    const up = () => { dragging.current = false; setIsDragging(false); document.body.style.userSelect = ''; document.body.style.cursor = ''; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);
  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);
  const toggleFs = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else playerRef.current?.requestFullscreen?.();
  }, []);

  // ThoughtPad notes — lifted here so a note pinned from the Chat tab appears in
  // the ThoughtPad tab immediately (single shared list). Keyed by the video id.
  const videoNotes = useVideoNotes(videoId || '');
  const { createNote: createVideoNote } = videoNotes;

  // Pin an AI answer from the Chat tab → a chat_answer note, then jump to the
  // ThoughtPad so the student sees it land (mirrors Dhyana's addToThoughtpad).
  const pinAnswer = useCallback(async (messageId: string, text: string) => {
    try {
      await createVideoNote({
        note_type: 'chat_answer' as NoteType,
        user_note: text,
        chat_message_id: messageId,
        note_metadata: { source: 'video', from_chat: true, timestamp_sec: Math.floor(currentTimeRef.current) },
      });
      setTab('thoughtpad');
    } catch { /* useVideoNotes surfaces the error (incl. not-linked) */ }
  }, [createVideoNote]);

  const retry = async () => {
    if (!videoId) return;
    setRetrying(true);
    try {
      await api.post(`/api/videos/${videoId}/ingest`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not start processing.');
    } finally {
      setRetrying(false);
    }
  };

  // ── jump-to chips (only the ones the lecture actually has) ───────────────
  const firstBlockSec = (type: string): number | null => {
    for (const t of topics) for (const b of (t.blocks || [])) if (b.type === type) return b.anchor_sec || t.start_sec;
    return null;
  };
  const jumps = useMemo(() => {
    const out: { label: string; icon: any; onClick: () => void }[] = [];
    const we = firstBlockSec('worked_example');
    if (we != null) out.push({ label: 'Worked example', icon: ListBulletIcon, onClick: () => navTo(we) });
    const cmp = firstBlockSec('comparison');
    if (cmp != null) out.push({ label: 'Comparison', icon: Squares2X2Icon, onClick: () => navTo(cmp) });
    if (digest?.formula_sheet?.length) out.push({ label: 'Formula sheet', icon: SparklesIcon, onClick: () => navTo(undefined, 'formula-sheet') });
    if (digest?.key_terms?.length) out.push({ label: 'Key terms', icon: DocumentTextIcon, onClick: () => navTo(undefined, 'key-terms') });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics, digest, navTo]);

  const stats = useMemo(() => {
    const we = topics.reduce((n, t) => n + (t.blocks || []).filter((b) => b.type === 'worked_example').length, 0);
    return [
      { n: topics.length, label: topics.length === 1 ? 'chapter' : 'chapters' },
      { n: we, label: we === 1 ? 'worked example' : 'worked examples' },
      { n: digest?.formula_sheet?.length || 0, label: 'formulas' },
      { n: digest?.key_terms?.length || 0, label: 'key terms' },
    ];
  }, [topics, digest]);

  const shell = isDarkMode ? 'bg-[#030712] text-white' : 'bg-gray-50 text-gray-900';
  const sub = isDarkMode ? 'text-slate-400' : 'text-gray-500';
  const card = isDarkMode ? 'border-slate-700/60 bg-slate-900/50' : 'border-gray-200 bg-white';

  const TABS: [TabKey, string, any][] = [
    ['overview', 'Overview', Squares2X2Icon],
    ['notes', 'Notes', DocumentTextIcon],
    ['thoughtpad', 'ThoughtPad', PencilSquareIcon],
    ['chat', 'Chat', ChatBubbleLeftRightIcon],
  ];

  return (
    <div className={cx('flex h-screen flex-col overflow-hidden', shell)}>
      {/* Top bar */}
      <div className={cx('flex flex-none items-center gap-3 border-b px-4 py-2.5', isDarkMode ? 'border-slate-800' : 'border-gray-200')}>
        <button onClick={() => fromNote ? navigate(-1) : navigate('/video-library')}
          className={cx('flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition', isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-600 hover:bg-gray-100')}>
          <ArrowLeftIcon className="h-4 w-4" /> {fromNote ? 'Back to note' : 'Library'}
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{video?.title || (loading ? 'Loading…' : 'Video')}</p>
          {video && (
            <p className={cx('truncate text-xs', sub)}>
              {video.book_title ? <>{video.book_title}{video.chapter_number ? ` · Chapter ${video.chapter_number}` : ''}</> : 'Untagged'}
            </p>
          )}
        </div>
        {video && (
          <span className={cx('inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold', isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-gray-100 text-gray-600')}>
            {yt ? <LinkIcon className="h-3.5 w-3.5" /> : <VideoCameraIcon className="h-3.5 w-3.5" />}{yt ? 'YouTube' : 'Upload'}
          </span>
        )}
      </div>

      {error && !video ? (
        <div className={cx('flex flex-1 items-center justify-center text-sm', sub)}>{error}</div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* LEFT — player */}
          <div className="flex min-w-0 flex-col p-4" style={{ width: `${leftPct}%` }}>
            <div ref={playerRef} className="group relative aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-lq-glass">
              {loading ? (
                <div className="h-full w-full animate-pulse bg-slate-800/50" />
              ) : yt ? (
                <iframe id="yt-frame" className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube.com/embed/${yt}?enablejsapi=1&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1&fs=0&iv_load_policy=3`}
                  title={video?.title || ''} frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              ) : video?.playback_url ? (
                <video ref={videoElRef} className="absolute inset-0 h-full w-full object-contain" src={video.playback_url} playsInline
                  onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                  onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
                  onLoadedMetadata={(e) => {
                    const d = e.currentTarget.duration; setDuration(d);
                    const r = resumeRef.current;
                    if (r > 3 && r < d - 5) { e.currentTarget.currentTime = r; setCurrentTime(r); }
                  }} />
              ) : (
                <div className="flex h-full items-center justify-center gap-2 text-sm text-slate-400"><PlayIcon className="h-5 w-5" /> Playback unavailable.</div>
              )}

              {/* ── our player chrome (native controls are hidden) ── */}
              {!loading && (yt || video?.playback_url) && (
                <>
                  {/* click anywhere to play/pause */}
                  <button onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} tabIndex={-1} className="absolute inset-0 z-10 cursor-pointer" />

                  {/* center play badge when paused */}
                  {!playing && (
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm transition group-hover:scale-105">
                        <PlayIcon className="ml-1 h-8 w-8" />
                      </span>
                    </div>
                  )}

                  {/* control bar — visible when paused, on hover when playing */}
                  <div className={cx('absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 pb-2.5 pt-10 transition-opacity duration-200',
                    playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100')}>
                    {/* seek bar + chapter ticks */}
                    <div className="group/bar relative h-1.5 w-full cursor-pointer rounded-full bg-white/25"
                      onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); if (duration) seek(((e.clientX - r.left) / r.width) * duration); }}>
                      <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${pct}%` }} />
                      {topics.map((t) => (
                        <span key={t.start_sec} className="group/tick absolute top-1/2 h-4 w-2 -translate-x-1/2 -translate-y-1/2"
                          style={{ left: `${duration ? (t.start_sec / duration) * 100 : 0}%` }}
                          onClick={(e) => { e.stopPropagation(); seek(t.start_sec); }}>
                          <span className="absolute left-1/2 top-1/2 h-2.5 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded bg-white/70 transition-colors group-hover/tick:bg-cyan-300" />
                          <span className="pointer-events-none absolute bottom-5 left-1/2 z-30 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white shadow-lg group-hover/tick:block">
                            {t.title}<span className="ml-1.5 tabular-nums text-cyan-300">{fmtTime(t.start_sec)}</span>
                          </span>
                        </span>
                      ))}
                      <span className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow" style={{ left: `${pct}%` }} />
                    </div>
                    {/* controls row */}
                    <div className="mt-2 flex items-center gap-3 text-white">
                      <button onClick={togglePlay} title={playing ? 'Pause' : 'Play'} className="rounded transition hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400">
                        {playing ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
                      </button>
                      <span className="text-xs font-medium tabular-nums">{fmtTime(currentTime)} <span className="text-white/55">/ {fmtTime(duration)}</span></span>
                      <div className="flex-1" />
                      <button onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'} className="rounded transition hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400">
                        {muted ? <SpeakerXMarkIcon className="h-5 w-5" /> : <SpeakerWaveIcon className="h-5 w-5" />}
                      </button>
                      <button onClick={toggleFs} title={isFs ? 'Exit full screen' : 'Full screen'} className="rounded transition hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400">
                        {isFs ? <ArrowsPointingInIcon className="h-5 w-5" /> : <ArrowsPointingOutIcon className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Concepts under the player — quiet, flat tags */}
            {video?.concept_tags && video.concept_tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {video.concept_tags.map((c) => (
                  <span key={c} className={cx('rounded-md px-2 py-0.5 text-[11px] font-medium', isDarkMode ? 'bg-slate-800/70 text-slate-400' : 'bg-gray-100 text-gray-500')}>
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div onMouseDown={() => { dragging.current = true; setIsDragging(true); document.body.style.userSelect = 'none'; document.body.style.cursor = 'col-resize'; }}
            className="group relative w-2.5 flex-none cursor-col-resize">
            <div className={cx('absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 rounded-full transition-colors',
              isDragging ? 'bg-cyan-500' : isDarkMode ? 'bg-slate-800 group-hover:bg-cyan-500/60' : 'bg-gray-200 group-hover:bg-cyan-500/60')} />
          </div>

          {/* RIGHT — companion panel */}
          <div className={cx('flex min-w-0 flex-1 flex-col border-l', isDarkMode ? 'border-slate-800' : 'border-gray-200')}>
            {/* Tab strip */}
            <div className={cx('flex flex-none items-center gap-0.5 border-b px-3 py-2', isDarkMode ? 'border-slate-800' : 'border-gray-200')}>
              {TABS.map(([k, lbl, Icon]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={cx('relative inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60',
                    tab === k ? (isDarkMode ? 'text-white' : 'text-gray-900') : cx(sub, isDarkMode ? 'hover:text-slate-200' : 'hover:text-gray-700'))}>
                  {tab === k && (
                    <motion.span layoutId="watchTabPill" transition={{ type: 'spring', stiffness: 520, damping: 40 }}
                      className={cx('absolute inset-0 rounded-lg', isDarkMode ? 'bg-slate-800' : 'bg-gray-100')} />
                  )}
                  <Icon className="relative z-10 h-4 w-4" /><span className="relative z-10">{lbl}</span>
                </button>
              ))}
              {duration > 0 && (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-cyan-500/10 px-2 py-1 text-[11px] font-semibold tabular-nums text-cyan-600 dark:text-cyan-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />{fmtTime(currentTime)}
                </span>
              )}
            </div>

            {/* Processing / failed banners apply to Overview + Notes */}
            <div className="flex min-h-0 flex-1 flex-col">
              {/* OVERVIEW */}
              <div className={cx('min-h-0 flex-1 overflow-y-auto scrollbar-slim', tab === 'overview' ? 'block tab-fade' : 'hidden')}>
                {loading ? (
                  <SkeletonPanel dark={isDarkMode} />
                ) : isProcessing ? (
                  <ProcessingState dark={isDarkMode} status={video?.status} />
                ) : isFailed ? (
                  <FailedState dark={isDarkMode} message={video?.error_message} onRetry={retry} retrying={retrying} />
                ) : !hasDigest ? (
                  <EmptyState dark={isDarkMode} />
                ) : (
                  <div className="space-y-5 p-4">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-600 dark:text-cyan-400">
                      <Squares2X2Icon className="h-3.5 w-3.5" />Lecture Overview
                    </div>

                    {/* progress card */}
                    <div className={cx('rounded-2xl border p-4 shadow-lq-glass', card)}>
                      <div className="flex items-end justify-between">
                        <div>
                          <div className={cx('text-[11px] font-medium uppercase tracking-wide', sub)}>Your progress</div>
                          <div className="mt-0.5 text-lg font-bold tabular-nums">{fmtTime(currentTime)} <span className={cx('text-sm font-medium', sub)}>/ {fmtTime(duration)}</span></div>
                        </div>
                        <div className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-3xl font-extrabold text-transparent">{pct}%</div>
                      </div>
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-500/20">
                        <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>

                    {/* TL;DR */}
                    {digest?.tldr && (
                      <section>
                        <SectionHead icon={SparklesIcon}>In one line</SectionHead>
                        <p className={cx('text-sm leading-relaxed', isDarkMode ? 'text-slate-300' : 'text-gray-700')}>{digest.tldr}</p>
                      </section>
                    )}

                    {/* Chapters — the navigable map */}
                    {topics.length > 0 && (
                      <section>
                        <SectionHead icon={ListBulletIcon} hint="tap to jump in the video & notes">Chapters</SectionHead>
                        <div className="space-y-1">
                          {topics.map((t, i) => {
                            const watched = currentTime >= t.end_sec - 0.5 && activeIdx !== i;
                            const active = activeIdx === i;
                            return (
                              <button key={`${t.start_sec}-${i}`} onClick={() => navTo(t.start_sec)}
                                className={cx('group flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60',
                                  active ? 'border-cyan-500/50 bg-cyan-500/10'
                                    : isDarkMode ? 'border-transparent hover:border-slate-700 hover:bg-slate-800/50' : 'border-transparent hover:border-gray-200 hover:bg-gray-50')}>
                                <span className={cx('flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-bold',
                                  watched ? 'bg-cyan-500 text-white' : active ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-300' : isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500')}>
                                  {watched ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className={cx('block truncate text-sm font-semibold', active && 'text-cyan-700 dark:text-cyan-200')}>{t.title}</span>
                                  {t.summary && <span className={cx('block truncate text-xs', sub)}>{t.summary}</span>}
                                </span>
                                <span className={cx('flex flex-none items-center gap-1 text-[11px] tabular-nums', sub)}>
                                  {fmtTime(t.start_sec)}<ChevronRightIcon className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" />
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    )}

                    {/* Jump to */}
                    {jumps.length > 0 && (
                      <section>
                        <SectionHead icon={ArrowRightIcon}>Jump to</SectionHead>
                        <div className="grid grid-cols-2 gap-2">
                          {jumps.map((j) => (
                            <button key={j.label} onClick={j.onClick}
                              className={cx('flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60',
                                isDarkMode ? 'border-slate-700/60 bg-slate-800/40 hover:border-cyan-500/40 hover:bg-slate-800' : 'border-gray-200 bg-white hover:border-cyan-500/40 hover:bg-gray-50')}>
                              <j.icon className="h-4 w-4 text-cyan-500" />{j.label}
                            </button>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Objectives */}
                    {digest?.objectives && digest.objectives.length > 0 && (
                      <section>
                        <SectionHead icon={CheckCircleIcon}>You'll be able to…</SectionHead>
                        <ul className="space-y-2">
                          {digest.objectives.map((o, i) => (
                            <li key={i} className="flex gap-2 text-sm">
                              <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none text-cyan-500" />
                              <span className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>{o.replace(/^You'll be able to\s*/i, '')}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {/* stats */}
                    <div className="grid grid-cols-4 gap-2">
                      {stats.map((s) => (
                        <div key={s.label} className={cx('rounded-xl border p-2.5 text-center', card)}>
                          <div className="text-lg font-bold tabular-nums">{s.n}</div>
                          <div className={cx('text-[10px] leading-tight', sub)}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* NOTES */}
              <div ref={notesScrollRef}
                onWheel={() => { pauseFollowUntil.current = Date.now() + 6000; }}
                onTouchMove={() => { pauseFollowUntil.current = Date.now() + 6000; }}
                onScroll={handleNotesScroll}
                className={cx('relative min-h-0 flex-1 overflow-y-auto scrollbar-slim', tab === 'notes' ? 'block tab-fade' : 'hidden')}>
                {notesTopChapter && digest && topics.length > 0 && (
                  <div className={cx('sticky top-0 z-20 flex items-center gap-2 border-b px-4 py-2 backdrop-blur-md',
                    isDarkMode ? 'border-slate-800 bg-[#030712]/85' : 'border-gray-200 bg-white/85')}>
                    <span className="truncate text-xs font-bold">{notesTopChapter.title}</span>
                    <button onClick={() => seek(notesTopChapter.sec)}
                      className="ml-auto inline-flex flex-none items-center gap-0.5 rounded-md bg-cyan-500/10 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-cyan-600 transition hover:bg-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60 dark:text-cyan-300">
                      <PlayIcon className="h-2.5 w-2.5" />{fmtTime(notesTopChapter.sec)}
                    </button>
                  </div>
                )}
                {loading ? <SkeletonPanel dark={isDarkMode} />
                  : isProcessing ? <ProcessingState dark={isDarkMode} status={video?.status} />
                  : isFailed ? <FailedState dark={isDarkMode} message={video?.error_message} onRetry={retry} retrying={retrying} />
                  : digest && topics.length > 0 ? (
                    <DigestNotes digest={digest as any} seek={seek} dark={isDarkMode} activeSec={activeTopic?.start_sec} />
                  ) : video?.lecture_notes_md ? (
                    <div className="px-4 py-4"><MarkdownRenderer content={video.lecture_notes_md} onTimestampClick={seek} className="text-sm" /></div>
                  ) : <EmptyState dark={isDarkMode} />}
              </div>

              {/* THOUGHTPAD — full Dhyana-parity notes (shared with the book's chapter) */}
              <div className={cx('min-h-0 flex-1 flex-col', tab === 'thoughtpad' ? 'flex tab-fade' : 'hidden')}>
                {video?.id && (
                  <VideoThoughtPad
                    notes={videoNotes.notes}
                    loading={videoNotes.loading}
                    notLinked={videoNotes.notLinked}
                    error={videoNotes.error}
                    fetchNotes={videoNotes.fetchNotes}
                    createNote={videoNotes.createNote}
                    deleteNote={videoNotes.deleteNote}
                    onSeek={seek}
                    getPlayhead={getPlayhead}
                    captureFrame={captureFrame}
                  />
                )}
              </div>

              {/* CHAT — video_chat (reuses the book-chat chat components) */}
              <div className={cx('min-h-0 flex-1 flex-col', tab === 'chat' ? 'flex tab-fade' : 'hidden')}>
                {/* All props are referentially stable except currentSection
                    (changes on chapter boundaries only) — so the memoized
                    panel skips the 4Hz playback re-renders entirely. */}
                {video?.id && (
                  <VideoChatPanel
                    videoId={video.id}
                    getPlayhead={getPlayhead}
                    onSeek={seek}
                    currentSection={activeTopic?.title || null}
                    captureFrame={captureFrame}
                    onPinAnswer={pinAnswer}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Drag shield — keeps mousemove flowing while resizing (iframes otherwise eat it) */}
      {isDragging && <div className="fixed inset-0 z-[60]" style={{ cursor: 'col-resize' }} />}
    </div>
  );
};

// ── small presentational helpers ───────────────────────────────────────────
const SectionHead: React.FC<{ icon: any; hint?: string; children: React.ReactNode }> = ({ icon: Icon, hint, children }) => (
  <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
    <Icon className="h-4 w-4 text-cyan-500" />{children}
    {hint && <span className="ml-1 text-[11px] font-normal text-slate-400">· {hint}</span>}
  </div>
);

const ProcessingState: React.FC<{ dark: boolean; status?: string }> = ({ dark, status }) => (
  <div className="flex h-full flex-col items-center justify-center px-8 text-center">
    <div className="mb-4 h-10 w-10 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
    <h3 className="text-base font-bold">Generating your notes…</h3>
    <p className={cx('mt-1 max-w-xs text-sm', dark ? 'text-slate-400' : 'text-gray-500')}>
      {status === 'uploaded' ? 'Queued for processing.' : 'Transcribing → writing notes → indexing.'} This page refreshes itself when it's ready.
    </p>
  </div>
);

const FailedState: React.FC<{ dark: boolean; message?: string | null; onRetry: () => void; retrying: boolean }> = ({ dark, message, onRetry, retrying }) => (
  <div className="flex h-full flex-col items-center justify-center px-8 text-center">
    <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500/15 text-red-500"><ExclamationTriangleIcon className="h-6 w-6" /></span>
    <h3 className="text-base font-bold">Processing failed</h3>
    <p className={cx('mt-1 max-w-xs text-sm', dark ? 'text-slate-400' : 'text-gray-500')}>{message || 'Something went wrong while generating the notes.'}</p>
    <button onClick={onRetry} disabled={retrying}
      className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-white shadow-glow-cyan transition hover:bg-cyan-600 disabled:opacity-60">
      <ArrowPathIcon className={cx('h-4 w-4', retrying && 'animate-spin')} />{retrying ? 'Starting…' : 'Retry processing'}
    </button>
  </div>
);

const EmptyState: React.FC<{ dark: boolean }> = ({ dark }) => (
  <div className="flex h-full flex-col items-center justify-center px-8 text-center">
    <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-500/15 text-slate-400"><DocumentTextIcon className="h-6 w-6" /></span>
    <h3 className="text-base font-bold">No notes yet</h3>
    <p className={cx('mt-1 max-w-xs text-sm', dark ? 'text-slate-400' : 'text-gray-500')}>This lecture is ready to watch, but its AI notes haven't been generated.</p>
  </div>
);

const SkeletonPanel: React.FC<{ dark: boolean }> = ({ dark }) => {
  const bar = dark ? 'bg-slate-800' : 'bg-gray-200';
  return (
    <div className="animate-pulse space-y-5 p-4">
      <div className={cx('h-24 w-full rounded-2xl', bar)} />
      <div className="space-y-2">
        <div className={cx('h-3 w-24 rounded', bar)} />
        <div className={cx('h-3 w-full rounded', bar)} />
        <div className={cx('h-3 w-11/12 rounded', bar)} />
        <div className={cx('h-3 w-4/5 rounded', bar)} />
      </div>
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => <div key={i} className={cx('h-12 w-full rounded-xl', bar)} />)}
      </div>
    </div>
  );
};

export default VideoWatch;
